import React from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";

import apiClient from "@dashboard/files/api/apiClient";
import { ChangesDialog } from "@dashboard/shared/components/ChangesDialog";
import { DropdownItem } from "@devices/components/shared";
import DropdownContainer from "@devices/components/shared/Dropdown/DropdownContainer";
import { PrimaryOutlinedButton } from "@shared/CustomButton";
import { HoverRow } from "@shared/table/HoverRow";
import { MinimalTableCell } from "@shared/table/MinimalTableComponents";
import { showErrorToast } from "@shared/toasts/Toasts";
import { flexRender, Row } from "@tanstack/react-table";

import { DownloadImageModal } from "./DownloadImageModal";
import { InformationModal } from "./InformationModal";

const useStyles = makeStyles()((theme) => ({
    actionContainer: {
        display: "flex",
        gap: theme.spacing(1),
        justifyContent: "flex-end",
    },
}));

interface TableItemProps {
    row: Row<API.ReleaseFile>;
    index: number;
}
const TableItem = ({ row }: TableItemProps) => {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [isChangesDialogOpen, setIsChangesDialogOpen] = React.useState(false);
    const [isDownloadModalOpen, setIsDownloadModalOpen] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const menuAnchorRef = React.useRef<HTMLButtonElement | null>(null);
    const { t } = useTranslation(["pages", "modals", "common"]);
    const { classes } = useStyles();

    const handleDownload = async () => {
        try {
            setLoading(true);
            await apiClient.invokeFileDownload(row.original.id, row.original.name ?? "", true);
            return true;
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message);
            return false;
        } finally {
            setLoading(false);
        }
    };

    return (
        <React.Fragment>
            <HoverRow {...row} clickable={false}>
                {row
                    .getVisibleCells()
                    .map((cell) => flexRender(cell.column.columnDef.cell, { ...cell.getContext(), key: cell.id }))}
                <MinimalTableCell align="right">
                    <div className={classes.actionContainer}>
                        <PrimaryOutlinedButton
                            name="download-file"
                            onClick={() => setIsDownloadModalOpen(true)}
                            tooltipProps={{
                                title: t("pages:downloadsPage.tooltips.downloadFiles"),
                            }}
                        >
                            <i className="fa-solid fa-download" />
                        </PrimaryOutlinedButton>
                        <DropdownContainer
                            icon={<i className="fa-solid fa-ellipsis-vertical" />}
                            anchorRef={menuAnchorRef}
                            tooltipProps={{ title: t("common:tooltip.moreActions") }}
                        >
                            <DropdownItem
                                label={t("pages:downloadsPage.tooltips.showChanges")}
                                iconName="fa-solid fa-fw fa-list"
                                onClick={() => setIsChangesDialogOpen(true)}
                            />
                            <DropdownItem
                                label={t("pages:downloadsPage.tooltips.showDetails")}
                                iconName="fa-solid fa-fw fa-circle-info"
                                onClick={() => setIsModalOpen(true)}
                            />
                        </DropdownContainer>
                    </div>
                </MinimalTableCell>
            </HoverRow>
            <DownloadImageModal
                file={row.original}
                open={isDownloadModalOpen}
                onClose={() => setIsDownloadModalOpen(false)}
                onDownload={handleDownload}
                loading={loading}
            />
            <InformationModal file={row.original} isOpen={isModalOpen} toggleDialog={() => setIsModalOpen(false)} />
            <ChangesDialog
                title={t("modals:changesDialog.titles.downloads")}
                onClose={() => setIsChangesDialogOpen(false)}
                changesWithDate={row.original.changesWithDate}
                open={isChangesDialogOpen}
            />
        </React.Fragment>
    );
};

export default TableItem;
